// Session BBC: the visit date window as the engineer's sheet applies it. A MIRROR of the server's one
// home, server/src/lib/visitDateWindow.ts, bound by visitDateWindow.parity.test.ts: free to
// VISIT_DATE_SOFT_DAYS back, a late reason up to VISIT_DATE_HARD_DAYS, older days not offered at all
// (the office records those). The server re-checks every sheet; this only keeps the phone from asking
// for something it would refuse. Pure.

import { daysBetweenLocalDates } from "./calloutSheet";

export const VISIT_DATE_SOFT_DAYS = 7;
export const VISIT_DATE_HARD_DAYS = 14;

export const LATE_REASONS = {
  access_delayed: "Customer access delayed",
  waiting_parts_paperwork: "Waiting on parts or paperwork",
  forgot: "Forgot to submit",
  other: "Other",
} as const;
export type LateReasonCode = keyof typeof LATE_REASONS;
export const LATE_REASON_CODES = Object.keys(LATE_REASONS) as LateReasonCode[];
export const LATE_REASON_TEXT_MAX = 200;

const YMD = /^\d{4}-\d{2}-\d{2}$/;

// Whole calendar days from one "YYYY-MM-DD" to another. Date strings, so a clock change never counts.
export function calendarDaysBetween(fromYmd: string, toYmd: string): number {
  if (!YMD.test(fromYmd) || !YMD.test(toYmd)) return 0;
  return Math.round((Date.parse(`${toYmd}T00:00:00Z`) - Date.parse(`${fromYmd}T00:00:00Z`)) / 86_400_000);
}

export type VisitAgeBand = "free" | "late" | "too_old";

export function visitAgeBand(days: number): VisitAgeBand {
  if (days > VISIT_DATE_HARD_DAYS) return "too_old";
  if (days > VISIT_DATE_SOFT_DAYS) return "late";
  return "free";
}

export function lateReasonGiven(code: string | null | undefined, text: string | null | undefined): boolean {
  if (!code || !(code in LATE_REASONS)) return false;
  return code !== "other" || (text ?? "").trim().length >= 3;
}

// The plain-English line the engineer is shown when a queued sheet meets the hard cut on arrival.
export const VISIT_DATE_TOO_OLD_ESCALATION = "This visit is now over 2 weeks old. Contact the office, who will record it.";

// The office's Late flag: how many calendar days after the visit a sheet arrived, when that is more than
// VISIT_DATE_SOFT_DAYS (the band where the engineer had to give a reason), else null. Reads the same two
// dates as the "Sent N days after the visit" line, so the flag and the line cannot disagree.
export function lateSubmissionDays(completedAt: string | null | undefined, receivedAt: string | null | undefined): number | null {
  const days = daysBetweenLocalDates(completedAt, receivedAt);
  return days > VISIT_DATE_SOFT_DAYS ? days : null;
}
